import type { HTMLAttributes } from "react";

import { BaseSkeleton } from "../base/BaseSkeleton";
import { cn } from "../base/utils";
import { SkeletonScope, SkeletonItem, type SkeletonGroupAnimation } from "../skeleton/motion";

export type WorkbenchGallerySkeletonLayout = "grid" | "rail";

export interface WorkbenchGallerySkeletonSection {
  id: string;
  layout?: WorkbenchGallerySkeletonLayout;
  filterCount?: number;
  cardCount?: number;
  aspectRatios?: readonly number[];
}

export interface WorkbenchGallerySkeletonProps extends HTMLAttributes<HTMLDivElement> {
  sections?: readonly WorkbenchGallerySkeletonSection[];
  animated?: boolean;
  animation?: SkeletonGroupAnimation;
}

const DEFAULT_SECTIONS: readonly WorkbenchGallerySkeletonSection[] = [
  {
    id: "primary",
    layout: "rail",
    filterCount: 2,
    cardCount: 4,
    aspectRatios: [16 / 11],
  },
  {
    id: "secondary",
    layout: "grid",
    filterCount: 3,
    cardCount: 5,
    aspectRatios: [4 / 5, 1, 3 / 4, 5 / 4, 1],
  },
];

function normalizeCount(value: number | undefined, fallback: number, maximum: number) {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return fallback;
  return Math.min(Math.round(value), maximum);
}

function normalizeAspectRatio(value: number | undefined) {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return 1;
  return Math.min(Math.max(value, 0.5), 2);
}

function WorkbenchGallerySkeletonHeading({
  animated,
  filterCount,
}: {
  animated: boolean;
  filterCount: number;
}) {
  return (
    <div className="flex min-h-10 flex-wrap items-center justify-between gap-4">
      <div className="space-y-2">
        <BaseSkeleton variant="line" animated={animated} className="h-6 w-36 rounded-md" />
        <BaseSkeleton variant="line" animated={animated} className="h-3 w-12" />
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        {Array.from({ length: filterCount }, (_, index) => (
          <BaseSkeleton
            key={index}
            variant="block"
            animated={animated}
            className="h-9 w-28 rounded-lg"
          />
        ))}
      </div>
    </div>
  );
}

export function WorkbenchGallerySkeleton({
  sections = DEFAULT_SECTIONS,
  animated = true,
  animation,
  className,
  ...props
}: WorkbenchGallerySkeletonProps) {
  return (
    <SkeletonScope animation={animation} animated={animated}>
      <div
        {...props}
        role="status"
        aria-busy="true"
        aria-label="Loading gallery"
        data-monkeys-component="workbench-gallery-skeleton"
        className={cn("grid w-full gap-4", className)}
      >
        {sections.map((section, sectionIndex) => {
          const layout = section.layout ?? "grid";
          const filterCount = normalizeCount(section.filterCount, 2, 8);
          const cardCount = normalizeCount(section.cardCount, layout === "rail" ? 4 : 5, 24);
          const ratios = section.aspectRatios?.length
            ? section.aspectRatios
            : [layout === "rail" ? 16 / 11 : 1];

          return (
            <SkeletonItem key={section.id || sectionIndex} index={sectionIndex}>
              <section className="grid gap-4" data-skeleton-section={section.id}>
                <WorkbenchGallerySkeletonHeading animated={animated} filterCount={filterCount} />
                <div
                  className={cn(
                    "grid items-start gap-4",
                    layout === "rail"
                      ? "grid-cols-1 sm:grid-cols-2 xl:grid-cols-4"
                      : "grid-cols-2 md:grid-cols-3 xl:grid-cols-5",
                  )}
                  data-skeleton-layout={layout}
                >
                  {Array.from({ length: cardCount }, (_, index) => (
                    <div
                      key={index}
                      className="overflow-hidden rounded-lg border border-border bg-card"
                    >
                      <BaseSkeleton
                        variant="block"
                        animated={animated}
                        className="h-auto w-full rounded-none"
                        style={{
                          aspectRatio: String(normalizeAspectRatio(ratios[index % ratios.length])),
                        }}
                      />
                    </div>
                  ))}
                </div>
              </section>
            </SkeletonItem>
          );
        })}
      </div>
    </SkeletonScope>
  );
}
